import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Slide
} from '@mui/material';
import {
  Close,
  LocationOn,
  Schedule,
  Euro,
  Favorite,
  FavoriteBorder,
  Share,
  Send,
  Business,
  CheckCircle
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import './MissionDetailModal.css';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MissionDetailModal = ({ open, onClose, mission, onApply, onFavorite, onShare }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFavorite, setIsFavorite] = React.useState(false);
  
  if (!mission) return null;
  
  const handleFavoriteClick = () => {
    setIsFavorite(!isFavorite);
    onFavorite(mission.id);
  };
  
  const handleApplyClick = () => {
    onApply(mission.id);
    onClose();
  };
  
  const handleShareClick = () => {
    onShare(mission);
  };
  
  return (
    <>
      <Helmet>
        <title>{mission.title} - Mission Sport</title>
        <meta name="description" content={mission.description} />
        <meta property="og:title" content={`${mission.title} - Mission Sport`} />
        <meta property="og:description" content={mission.description} />
        <meta property="og:image" content={mission.image} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Transition}
        className="mission-detail-modal"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            maxHeight: isMobile ? '100vh' : '90vh'
          }
        }}
      >
        <Box className="modal-header">
          <IconButton
            onClick={onClose}
            className="close-button"
            aria-label="Fermer"
          >
            <Close />
          </IconButton>
          
          <Box className="header-actions">
            <IconButton
              onClick={handleFavoriteClick}
              className="favorite-button"
              aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
            >
              {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
            </IconButton>
            
            <IconButton
              onClick={handleShareClick}
              className="share-button"
              aria-label="Partager"
            >
              <Share />
            </IconButton>
          </Box>
        </Box>
        
        <DialogContent className="modal-content" sx={{ p: 0 }}>
          {/* Hero Image */}
          <Box className="mission-hero-image">
            <img
              src={mission.image}
              alt={mission.title}
              loading="lazy"
            />
            <Box className="image-overlay">
              <Chip
                label={mission.type}
                className="mission-type-chip"
                size="small"
              />
            </Box>
          </Box>
          
          {/* Content */}
          <Box className="modal-body" sx={{ p: 3 }}>
            {/* Title and Organization */}
            <Box className="mission-header" sx={{ mb: 3 }}>
              <Typography variant="h4" className="mission-title" gutterBottom>
                {mission.title}
              </Typography>
              
              <Box className="organization-info" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar className="organization-avatar">
                  <Business />
                </Avatar>
                <Typography variant="subtitle1" color="text.secondary">
                  {mission.organization?.name || mission.organization}
                </Typography>
                <Chip
                  icon={<CheckCircle />}
                  label="Vérifié"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            </Box>
            
            {/* Key Information */}
            <Box className="mission-info-grid" sx={{ mb: 3 }}>
              <Box className="info-item">
                <LocationOn className="info-icon" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Localisation
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {mission.location}
                  </Typography>
                </Box>
              </Box>
              
              <Box className="info-item">
                <Schedule className="info-icon" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {mission.date}
                  </Typography>
                </Box>
              </Box>
              
              <Box className="info-item">
                <Euro className="info-icon" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Tarif
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" className="rate-text">
                    {mission.rate}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Description */}
            <Box className="mission-description" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {mission.description}
              </Typography>
              <Typography variant="body1" paragraph>
                Cette mission offre une excellente opportunité de développer vos compétences dans le domaine du {mission.discipline.toLowerCase()}. 
                Vous travaillerez au sein d'une équipe dynamique et bénéficierez d'un environnement professionnel stimulant.
              </Typography>
            </Box>
            
            {/* Requirements */}
            <Box className="mission-requirements" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Compétences requises
              </Typography>
              <Box className="requirements-list">
                {mission.requirements?.map((requirement, index) => (
                  <Chip
                    key={index}
                    label={requirement}
                    variant="outlined"
                    className="requirement-chip"
                  />
                ))}
              </Box>
            </Box>
            
            {/* Discipline */}
            <Box className="mission-discipline" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Discipline
              </Typography>
              <Chip
                label={mission.discipline}
                color="primary"
                size="large"
                className="discipline-chip"
              />
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Additional Information */}
            <Box className="additional-info">
              <Typography variant="h6" gutterBottom>
                Informations complémentaires
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Possibilité d'évolution vers un poste permanent
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Formation continue assurée par l'organisation
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Équipements et matériel fournis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Remboursement des frais de transport
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions className="modal-actions" sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            className="cancel-button"
            size="large"
          >
            Fermer
          </Button>
          
          <Button
            variant="contained"
            onClick={handleApplyClick}
            startIcon={<Send />}
            className="apply-button"
            size="large"
          >
            Postuler
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MissionDetailModal;